import { useCallback, useEffect, useRef, useState } from "react";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { getAuthSession } from "../services/auth/auth-session";
import { signInWithGoogle } from "../services/auth/google-auth";

// Trigger One Tap 5 minutes before the session expires.
const REFRESH_BEFORE_EXPIRY_SECONDS = 5 * 60;
const nowSeconds = () => Math.floor(Date.now() / 1000);

// Inner component — mounts only when a refresh is needed, which causes
// useGoogleOneTapLogin to fire. Unmounting and remounting with a new key
// lets us re-trigger it for subsequent refreshes.
function OneTapTrigger({ onSuccess, onError }) {
  useGoogleOneTapLogin({
    auto_select: true,
    cancel_on_tap_outside: false,
    onSuccess,
    onError,
  });
  return null;
}

export default function GoogleAutoRefresh() {
  const [triggerKey, setTriggerKey] = useState(0);
  const [active, setActive] = useState(false);
  const timerRef = useRef(null);

  const schedule = useCallback(() => {
    clearTimeout(timerRef.current);
    const session = getAuthSession();
    if (!session?.expiresAt || session?.provider !== "google") {
      return;
    }

    const refreshAt = session.expiresAt - REFRESH_BEFORE_EXPIRY_SECONDS;
    const delayMs = Math.max(0, (refreshAt - nowSeconds()) * 1000);

    timerRef.current = setTimeout(() => {
      setActive(true);
      setTriggerKey((k) => k + 1);
    }, delayMs);
  }, []);

  useEffect(() => {
    schedule();
    return () => clearTimeout(timerRef.current);
  }, [schedule]);

  const handleSuccess = async (credentialResponse) => {
    setActive(false);
    try {
      await signInWithGoogle(credentialResponse.credential);
      window.dispatchEvent(new Event("puck-pickup-auth-change"));
      // eslint-disable-next-line no-console
      console.log("[auth] silent Google session refresh succeeded");
      schedule();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[auth] silent Google session refresh failed", err);
    }
  };

  const handleError = () => {
    setActive(false);
    // One Tap was suppressed or user is not logged into Google.
    // The session will expire naturally — hasLocallyValidSession() will
    // return false and the nav bar will show the Login button.
    // eslint-disable-next-line no-console
    console.log("[auth] One Tap silent refresh unavailable — session will expire naturally");
  };

  if (!active) return null;

  return (
    <OneTapTrigger
      key={triggerKey}
      onSuccess={handleSuccess}
      onError={handleError}
    />
  );
}
